import * as React from 'react'

import { type IParseFile, type UploadOptions, nuvoSession } from 'nuvo-react'

const INVALID_SHEET_PREFIXES = ['Lang', 'v_']

interface UseNuvoSessionProps {
  importType: string
  parsedFile: IParseFile[] | undefined
  initialData?: Record<string, string | string[]>[]
  loading: boolean
  nuvoImporterRef: React.RefObject<HTMLDivElement>
}

export const useNuvoSession = ({
  importType,
  parsedFile,
  initialData,
  loading,
  nuvoImporterRef,
}: UseNuvoSessionProps) => {
  React.useEffect(() => {
    if (!parsedFile && (loading || !!initialData)) return

    const getSessionData = (): UploadOptions => {
      if (!parsedFile) return { step: 'mapping', data: [] }

      const validSheets =
        parsedFile?.[0]?.sheets?.filter((sheet) => {
          return !INVALID_SHEET_PREFIXES.some((prefix) => sheet.sheetName.startsWith(prefix))
        }) || []

      const numberOfSheets = validSheets.length
      const getStep = () => {
        if (numberOfSheets && numberOfSheets > 1) {
          return 'sheetSelection'
        }

        return 'mapping'
      }
      const getData = () => {
        if (numberOfSheets && numberOfSheets > 1) {
          return parsedFile
        }

        return parsedFile?.[0]?.sheets[0]?.data || []
      }

      return {
        step: getStep(),
        data: getData(),
      }
    }

    if (parsedFile) {
      nuvoSession.upload(getSessionData(), importType)
      nuvoSession.start(importType)
    }
  }, [parsedFile, importType, initialData, loading])

  React.useEffect(() => {
    if (!initialData || loading || !nuvoImporterRef.current) return

    const timeoutId = setTimeout(() => {
      const nuvoElements = nuvoImporterRef.current?.querySelector('.nuvo-tailwind')

      if (!nuvoElements) {
        throw new Error('Nuvo elements not found')
      }

      nuvoSession.upload(
        {
          step: 'review',
          data: initialData,
          headerIndex: undefined,
        },
        importType
      )
      nuvoSession.start(importType)
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [initialData, importType, loading, nuvoImporterRef])
}
